import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { IOrderClause } from '@tableau/data-catalog-client-components';

import { ServiceLocator } from '../../../app/ServiceLocator';
import { DataGridCells } from '../../../app/spaces/ListView/DataGridCells';
import { SortableHeader } from '../../../app/spaces/ListView/SortableHeader';
import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { getHeightForRows, SortableDataGrid } from '../../../common/react/components/dataGrid/SortableDataGrid3';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { cssClass, flexColumnStyle } from '../../../style';
import {
  IPublishedConnectionDetailInfo,
  IPublishedConnectionTableInfo
} from '../../../ts/models/IPublishedConnectionInfo';
import { ISliceable } from '../../../ts/models/ISliceable';
import { SliceableFactory } from '../../../ts/services/SliceableFactory';
import { TablesGridDataKey } from './PublishedConnectionTablesGridTypes';

const cssPrefix = 'PublishedConnectionTablesGrid';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle, {
  position: 'relative',
  minHeight: getHeightForRows(2),
  paddingBottom: CommonStyles.Sizes.LargeSpace,
  borderBottom: '1px solid black',
  width: '100%'
});

function getRowData(
  tables: IPublishedConnectionTableInfo[],
  sliceableFactory: SliceableFactory,
  currentSort: IOrderClause
): (orderClause: IOrderClause[]) => ISliceable<any> {

  const tableRows: any[] = tables.map((table: IPublishedConnectionTableInfo) => {
    return {
      NAME: table.tableName,
      POLICIES: Messages.publishedConnectionNoPolicies(),
      EXTRACT: Messages.publishedConnectionLive(),
      DATABASE: table.databaseName,
      SCHEMA: table.schemaName
    };
  });
  if (currentSort.ascending) {
    tableRows.sort((a, b) => (a[currentSort.field] > b[currentSort.field]) ? 1 : -1);
  } else {
    tableRows.sort((a, b) => (a[currentSort.field] > b[currentSort.field]) ? -1 : 1);
  }
  return (_orderClause: IOrderClause[]) => sliceableFactory.fromArray(tableRows);
}

function hasDatabase(tables: IPublishedConnectionTableInfo[]): boolean {
  return tables.length > 0 && !!tables[0].databaseName;
}

function hasSchema(tables: IPublishedConnectionTableInfo[]): boolean {
  return tables.length > 0 && !!tables[0].schemaName;
}

function getTableColumns(tables: IPublishedConnectionTableInfo[], sortableHeaderFormatter: React.Factory<any>): DataGrid.Column[] {
  const nameColumn: DataGrid.Column = {
    name: TablesGridDataKey.NAME,
    dataKey: TablesGridDataKey.NAME,
    sortable: true,
    headerText: Messages.column_name(),
    growToFit: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.PlainTextCell(TablesGridDataKey.NAME)
  };
  const typeColumn = {
    name: TablesGridDataKey.TYPE,
    dataKey: TablesGridDataKey.TYPE,
    resizable: false,
    sortable: false,
    headerText: '',
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.ContentTypeCell({
      imageUrlForRow: () => imgPath('published_connection_table_medium.svg'),
      titleForIcon: () => Messages.table(),
      isCertified: () => false,
      getTooltipProps: () => null
    })
  };
  const extractColumn = {
    name: TablesGridDataKey.EXTRACT,
    dataKey: TablesGridDataKey.EXTRACT,
    headerText: Messages.extractOrLive(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.PlainTextCell(TablesGridDataKey.EXTRACT)
  };
  const policiesColumn = {
    name: TablesGridDataKey.POLICIES,
    dataKey: TablesGridDataKey.POLICIES,
    headerText: Messages.policiesApplied(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.PlainTextCell(TablesGridDataKey.POLICIES)
  };
  const databaseColumn = {
    name: TablesGridDataKey.DATABASE,
    dataKey: TablesGridDataKey.DATABASE,
    headerText: Messages.databaseName(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.PlainTextCell(TablesGridDataKey.DATABASE)
  };

  const schemaColumn = {
    name: TablesGridDataKey.SCHEMA,
    dataKey: TablesGridDataKey.SCHEMA,
    headerText: Messages.schemaName(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: DataGridCells.PlainTextCell(TablesGridDataKey.SCHEMA)
  };

  const columns: DataGrid.Column[] = [
    typeColumn,
    nameColumn
  ];
  if (hasDatabase(tables)) {
    columns.push(databaseColumn);
  }
  if (hasSchema(tables)) {
    columns.push(schemaColumn);
  }
  columns.push(extractColumn);
  columns.push(policiesColumn);
  return columns;
}

export type PublishedConnectionTablesGridState = Readonly<{
  currentSort: IOrderClause;
}>;

export type PublishedConnectionTablesGridProps = Readonly<{
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
}>;

export class PublishedConnectionTablesGrid extends React.PureComponent<PublishedConnectionTablesGridProps, PublishedConnectionTablesGridState> {
  private memoizedGetRowData = defaultMemoize(getRowData);
  private memoizedTableColumns = defaultMemoize(getTableColumns);
  private readonly sliceableFactory: SliceableFactory;

  constructor(props: PublishedConnectionTablesGridProps) {
    super(props);
    this.sliceableFactory = ServiceLocator.get<SliceableFactory>('SliceableFactory');
    this.state = {
      currentSort:
        {
          field: TablesGridDataKey.NAME,
          ascending: true
        },
    };
  }

  private emptyChild(): JSX.Element {
    return (
      <div className={'tb-data-grid-empty'}>
        <div className={'tb-data-grid-empty-title'}>{Messages.publishedConnectionTablesEmpty()}</div>
      </div>
    );
  }

  private changeSort = (field: string) => {
    if (field === this.state.currentSort.field) {
      this.setState({
        currentSort:
          {
            field: field,
            ascending: !this.state.currentSort.ascending
          }
      });
    } else {
      this.setState({
        currentSort:
          {
            field: field,
            ascending: true
          }
      });
    }
  };

  public render(): JSX.Element {
    const numOfRows: number = 0;
    const sortableHeaderFormatter = SortableHeader.Formatter([this.state.currentSort], this.changeSort);
    const sliceable: (orderClause: IOrderClause[]) => ISliceable<any> = this.memoizedGetRowData(
      this.props.publishedConnectionDetailInfo.tables,
      this.sliceableFactory,
      this.state.currentSort
    );
    const heightStyle: React.CSSProperties = {
      height: numOfRows ? getHeightForRows(numOfRows + 1) : undefined
    };
    const columns = this.memoizedTableColumns(this.props.publishedConnectionDetailInfo.tables, sortableHeaderFormatter);

    return (
      <div className={rootClass} style={heightStyle}>
        <SortableDataGrid
          columns={columns}
          getSliceable={sliceable}
          emptyChild={this.emptyChild()}
        />
      </div>
    );
  }
}
